import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Pressable, Platform, Image, TextInput, Switch, PanResponder, useWindowDimensions } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import Svg, { Path, G } from 'react-native-svg';
import SvgCanvas from '../SvgCanvas.web';
import { parseDailySvg, getSvgDimensions, LayerData } from '../../core/svgParser';
import * as Clipboard from 'expo-clipboard';
import * as ImagePicker from 'expo-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { colors, typography } from '../../styles/theme';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { mockPacks } from './PackBrowserModal';
import AdvancedSettingsInfoModal from './AdvancedSettingsInfoModal';

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  subtitleText: {
    ...typography.body,
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    marginBottom: 18,
  },
  imageContainer: {
    alignItems: 'center',
    marginBottom: 20,
    marginLeft: 20,
    marginRight: 20,
  },
  pipelineGraphic: {
    width: '100%',
    height: 250, // Gives it a fixed box to live inside. Adjust this number to dial in the vertical spacing.
  },
  section: {
    gap: 16,
    paddingBottom: 20,
  },
  sectionCenter: {
    alignItems: 'center',
    gap: 12,
  },
  bodyText: {
    ...typography.body,
    color: colors.textMuted,
    textAlign: 'center',
  },
  valuePropsContainer: {
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 20,
    gap: 12,
    marginBottom: 8,
    marginTop: 32,
  },
  valuePropRow: {
    fontSize: 16,
  },
  valuePropText: {
    ...typography.body,
    color: colors.text,
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.text,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 0,
    width: '100%',
    alignItems: 'center',
  },
  secondaryButtonText: {
    ...typography.bodyBold,
    color: colors.text,
    letterSpacing: 0.3,
  },
  loadingText: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 14,
  },
  errorText: {
    ...typography.body,
    color: '#d32f2f',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 8,
  },
  sliderContainer: {
    marginBottom: 16,
    width: '100%',
  },
  sliderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sliderLabel: {
    ...typography.bodyBold,
    color: colors.text,
    fontSize: 14,
  },
  sliderValue: {
    ...typography.bodyBold,
    color: colors.primary,
    fontSize: 14,
  },
  sliderTrackContainer: {
    height: 32,
    justifyContent: 'center',
    width: '100%',
  },
  sliderTrack: {
    height: 8,
    backgroundColor: colors.barBackground,
    borderRadius: 4,
    width: '100%',
    position: 'relative',
  },
  sliderFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 4,
  },
  sliderThumb: {
    width: 20,
    height: 20,
    backgroundColor: colors.primary,
    borderRadius: 10,
    position: 'absolute',
    top: -6,
    marginLeft: -10,
    boxShadow: `1px 1px 2px ${colors.shadow}`,
    elevation: 2,
  },
  slidersList: {
    paddingHorizontal: 0,
  },
  previewContainer: {
    alignSelf: 'center',
    backgroundColor: colors.surface,
    padding: 20,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 20,
    borderWidth: 0,
    borderColor: colors.border,
    boxShadow: `2px 2px 0px ${colors.shadow}`,
    elevation: 2,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  previewContainerZoomed: {
    width: '100%',
    alignSelf: 'stretch',
    padding: 20, // Equal padding on all sides
  },
  previewImage: {
    width: 400,
    height: 200,
  },
  previewImageZoomed: {
    width: '100%',
    height: '100%',
  },
  zoomButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    ...Platform.select({
      web: { boxShadow: `1px 1px 4px ${colors.shadow}` } as any,
      default: { elevation: 4 }
    }),
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    width: '100%',
  },
  toggleLabel: {
    ...typography.bodyBold,
    color: colors.text,
    fontSize: 14,
  },
  urlField: {
    width: '100%',
    backgroundColor: colors.surface,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: `2px 2px 0px ${colors.shadow}`,
    elevation: 3,
  },
  urlText: {
    ...typography.body,
    color: colors.text,
    fontSize: 14,
  },
  urlWrapper: {
    width: '100%',
    position: 'relative',
    alignItems: 'center',
    marginBottom: 8,
  },
  copyTooltipContainer: {
    position: 'absolute',
    bottom: '100%',
    marginBottom: -20,
    alignItems: 'center',
    zIndex: 100,
  },
  copyTooltipBox: {
    backgroundColor: '#333333',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  copyTooltipText: {
    ...typography.body,
    color: colors.surface,
    fontSize: 12,
    fontWeight: '600',
  },
  copyTooltipArrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#333333',
  },
  controlsSection: {
    width: '100%',
    gap: 20,
  },
  selectorContainer: {
    marginBottom: 18,
  },
  selectorLabel: {
    ...typography.bodyBold,
    color: colors.text,
    fontSize: 14,
    marginBottom: 8,
  },
  webSelect: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    fontFamily: typography.body.fontFamily,
    fontSize: 16,
    color: colors.text,
    outlineStyle: 'none',
  } as any,
  nativeSelectorPlaceholder: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 0,
  },
  accordionTitle: {
    ...typography.bodyBold,
    color: colors.text,
    fontSize: 15,
    marginBottom: 0,
  },
  link: {
    alignSelf: 'flex-start',
    marginBottom: 20,
  },
  linkText: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  textInput: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    fontFamily: typography.body.fontFamily,
    fontSize: 16,
    color: colors.text,
  },
  collisionBanner: {
    backgroundColor: '#fff3e0',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#ffe0b2',
    marginBottom: 16,
    gap: 12,
  },
  collisionTitle: {
    ...typography.bodyBold,
    color: '#e65100',
    fontSize: 14,
  },
  collisionText: {
    ...typography.body,
    color: '#ef6c00',
    fontSize: 13,
  },
  collisionActions: {
    flexDirection: 'row',
    gap: 12,
  },
  collisionButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  collisionButtonText: {
    ...typography.bodyBold,
    fontSize: 13,
  },
  overrideButton: {
    backgroundColor: '#e65100',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#e65100',
  },
});

interface ParameterSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onValueChange: (value: number) => void;
}

const ParameterSlider = ({ label, value, min, max, step, onValueChange }: ParameterSliderProps) => {
  const [trackWidth, setTrackWidth] = useState(0);

  const handlePress = (e: any) => {
    if (trackWidth === 0) return;
    const locationX = e.nativeEvent.locationX ?? e.nativeEvent.offsetX;
    if (locationX === undefined) return;

    const percentage = Math.max(0, Math.min(1, locationX / trackWidth));
    const newValue = min + percentage * (max - min);
    const steppedValue = Math.round(newValue / step) * step;
    onValueChange(parseFloat(steppedValue.toFixed(2)));
  };

  return (
    <View style={styles.sliderContainer}>
      <View style={styles.sliderHeader}>
        <Text style={styles.sliderLabel}>{label}</Text>
        <Text style={styles.sliderValue}>{value.toFixed(step < 1 ? 1 : 0)}</Text>
      </View>
      <View style={styles.sliderTrackContainer}>
        <View
          style={styles.sliderTrack}
          onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
          onStartShouldSetResponder={() => true}
          onResponderGrant={handlePress}
          onResponderMove={handlePress}
        >
          <View style={[styles.sliderFill, { width: `${((value - min) / (max - min)) * 100}%` }]} />
          <View style={[styles.sliderThumb, { left: `${((value - min) / (max - min)) * 100}%` }]} />
        </View>
      </View>
    </View>
  );
};

interface CustomModelModalProps {
  visible: boolean;
  onClose: () => void;
  onBack?: () => void;
  hasPicloPlay: boolean;
  title: string;
  onPlayPuzzle?: (svgUrl: string, isCustom: boolean, puzzleId?: string) => void;
  onSimulatePurchase?: () => void;
  customPuzzleCount: number;
  onRefreshEntitlements?: () => Promise<void>;
  isLoading?: boolean;
}

const CollisionWarning = ({ name, onConfirm, onCancel }: { name: string, onConfirm: () => void, onCancel: () => void }) => (
  <View style={styles.collisionBanner}>
    <Text style={styles.collisionTitle}>
      <FontAwesome6 name="triangle-exclamation" size={14} color="#e65100" /> Name Conflict
    </Text>
    <Text style={styles.collisionText}>
      A puzzle named "{name}" already exists in this pack. Do you want to overwrite it?
    </Text>
    <View style={styles.collisionActions}>
      <TouchableOpacity style={[styles.collisionButton, styles.overrideButton]} onPress={onConfirm}>
        <Text style={[styles.collisionButtonText, { color: '#fff' }]}>Overwrite</Text>
      </TouchableOpacity>
      <TouchableOpacity style={[styles.collisionButton, styles.cancelButton]} onPress={onCancel}>
        <Text style={[styles.collisionButtonText, { color: '#e65100' }]}>Cancel</Text>
      </TouchableOpacity>
    </View>
  </View>
);

type ModalState = 'locked' | 'ready' | 'loading' | 'complete';

export default function CustomModelModal({
  visible,
  onClose,
  onBack,
  hasPicloPlay,
  title,
  onPlayPuzzle,
  onSimulatePurchase,
  customPuzzleCount,
  onRefreshEntitlements,
  isLoading
}: CustomModelModalProps) {
  const { width } = useWindowDimensions();
  const isMobile = width <= 600;

  const [unlockKey, setUnlockKey] = useState<string | null>(null);
  const isAuthed = hasPicloPlay && !!unlockKey;
  const [modalState, setModalState] = useState<ModalState>(isAuthed ? 'ready' : 'locked');
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Vector Parameter States
  const [maxColors, setMaxColors] = useState(8);
  const [minDustArea, setMinDustArea] = useState(40);
  const [rdpEpsilon, setRdpEpsilon] = useState(1.0);
  const [cornerAngle, setCornerAngle] = useState(1.8);
  const [maxError, setMaxError] = useState(4.0);
  const [autoCrop, setAutoCrop] = useState(false);

  // Image & Preview States
  const [selectedImageUri, setSelectedImageUri] = useState<string | null>(null);
  const [previewThumbnailUrl, setPreviewThumbnailUrl] = useState<string | null>(null);
  const [previewFullUrl, setPreviewFullUrl] = useState<string | null>(null);
  const [blankThumbnailUrl, setBlankThumbnailUrl] = useState<string | null>(null);
  const [finalSvgUrl, setFinalSvgUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [customPuzzleId, setCustomPuzzleId] = useState<string | null>(null);
  const [customUrl, setCustomUrl] = useState<string>("");

  // Pack States
  const [packs, setPacks] = useState<{ id: string, title: string }[]>(mockPacks.map(p => ({ id: p.id, title: p.title })));
  const [selectedPack, setSelectedPack] = useState<string>(mockPacks[0].id);
  const [puzzleName, setPuzzleName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaveSuccess, setIsSaveSuccess] = useState(false);
  const [tempPrefix, setTempPrefix] = useState<string | null>(null);
  const [existingPuzzles, setExistingPuzzles] = useState<string[]>([]);
  const [showCollisionWarning, setShowCollisionWarning] = useState(false);
  const [collisionName, setCollisionName] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [previewLayers, setPreviewLayers] = useState<LayerData[]>([]);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [previewDimensions, setPreviewDimensions] = useState({ width: 800, height: 1067 });

  useEffect(() => {
    const fetchSvg = async () => {
      if (!finalSvgUrl) {
        setPreviewLayers([]);
        return;
      }
      try {
        const response = await fetch(finalSvgUrl);
        if (response.ok) {
          const xml = await response.text();
          const layers = parseDailySvg(xml);
          setPreviewLayers(layers);
          setPreviewDimensions(getSvgDimensions(xml));
        }
      } catch (err) {
        console.error('Error fetching preview SVG:', err);
      }
    };
    fetchSvg();
  }, [finalSvgUrl]);

  const apiUrl = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:3000';

  // Load unlock key from AsyncStorage whenever the modal opens
  useEffect(() => {
    if (visible) {
      AsyncStorage.getItem('@piclo:unlock_key').then((key) => {
        setUnlockKey(key);
      }).catch(() => setUnlockKey(null));
    }
  }, [visible]);

  useEffect(() => {
    setModalState((hasPicloPlay && !!unlockKey) ? 'ready' : 'locked');
  }, [hasPicloPlay, unlockKey]);

  useEffect(() => {
    const fetchPuzzles = async () => {
      if (!selectedPack) return;
      try {
        const response = await fetch(`${process.env.EXPO_PUBLIC_BUCKET_URL}/${selectedPack}/manifest.json?t=${Date.now()}`);
        if (!response.ok) throw new Error('Failed to fetch puzzles');
        const data = await response.json();
        let corrected = data;
        if (selectedPack === 'dogs 2') {
          corrected = data.map((id: string) => id.replace(/^dogs-/, 'dogs 2-'));
        } else if (selectedPack === 'cats 2') {
          corrected = data.map((id: string) => id.replace(/^butterflies-/, 'cats 2-'));
        }
        setExistingPuzzles(corrected);

        // Suggest a unique name if current name is empty or was a previous suggestion
        const safeName = puzzleName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
        if (!puzzleName || corrected.includes(safeName)) {
          const baseName = "Custom Puzzle";
          let counter = 1;
          let suggested = baseName;
          let suggestedSafe = suggested.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');

          while (corrected.includes(suggestedSafe)) {
            counter++;
            suggested = `${baseName} ${counter}`;
            suggestedSafe = suggested.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
          }
          setPuzzleName(suggested);
        }
      } catch (err) {
        console.error('Error fetching puzzles:', err);
      }
    };
    if (visible && selectedPack) {
      fetchPuzzles();
    }
  }, [selectedPack, visible, apiUrl]);

  const handleCopy = async () => {
    try {
      await Clipboard.setStringAsync(customUrl);
      setShowCopyTooltip(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setShowCopyTooltip(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleUpload = async () => {
    if (customPuzzleCount >= 5) {
      setShowOverwriteWarning(true);
      return;
    }
    await launchImagePicker();
  };

  const resumeUpload = async () => {
    setShowOverwriteWarning(false);
    await launchImagePicker();
  };

  const launchImagePicker = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const selectedUri = result.assets[0].uri;
        setSelectedImageUri(selectedUri);
        setPreviewThumbnailUrl(null); // Clear old preview if re-picking
        setPreviewFullUrl(null);
        setPreviewLayers([]);
        setIsZoomed(false);
        setErrorMsg(null);
        setIsDirty(true);
        // Automatically process upon selection
        handleProcess(selectedUri);
      }
    } catch (err) {
      console.error('Failed to pick image:', err);
      setErrorMsg('Failed to open image picker. Contact support@piclo.art if the problem persists.');
    }
  };

  const handleProcess = async (directUri?: string) => {
    const uriToProcess = directUri || selectedImageUri;
    if (!uriToProcess) return;

    // Hard auth guard — prevent any network call without a valid key
    if (!unlockKey) {
      setErrorMsg('Missing unlock key. Please purchase Piclo Play to create custom puzzles. Contact support@piclo.art if the problem persists.');
      return;
    }

    setIsProcessing(true);
    setPreviewLayers([]);
    setIsZoomed(false);
    setErrorMsg(null);

    try {
      const manipResult = await manipulateAsync(
        uriToProcess,
        [{ resize: { width: 800 } }],
        { compress: 0.8, format: SaveFormat.JPEG, base64: true }
      );

      const response = await fetch('https://piclo-engine.vercel.app/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageBase64: manipResult.base64 }),
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.svg) {
        const puzzleId = `custom_${Date.now()}`;
        setCustomPuzzleId(puzzleId);

        const cacheStr = await AsyncStorage.getItem('@piclo:puzzleData');
        const cache = cacheStr ? JSON.parse(cacheStr) : {};

        cache[puzzleId] = {
          title: puzzleName.trim() || 'Custom Puzzle',
          isCompleted: false,
          images: []
        };

        await AsyncStorage.setItem('@piclo:puzzleData', JSON.stringify(cache));

        try {
          const currentKey = unlockKey || await AsyncStorage.getItem('@piclo:unlock_key');
          if (!currentKey) throw new Error('Missing unlock key');
          const cfResponse = await fetch('https://piclo-entitlements.jscottkuehnert.workers.dev/api/save-puzzle', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Pass the puzzleId in the body!
            body: JSON.stringify({ unlockKey: currentKey, svgData: data.svg, puzzleId: puzzleId }),
          });

          if (!cfResponse.ok) {
            throw new Error(`Cloudflare rejected with status ${cfResponse.status}. Contact support@piclo.art if the problem persists.`);
          }

          const cfData = await cfResponse.json();
          if (cfData.success && cfData.url) {
            setCustomUrl(cfData.url);
            void onRefreshEntitlements?.();
          } else {
            throw new Error(cfData.error || 'Cloudflare failed to return URL. Contact support@piclo.art if the problem persists.');
          }
        } catch (cfErr) {
          console.error("Cloudflare Save Error:", cfErr);
          setErrorMsg("Puzzle generated locally, but failed to upload to cloud: " + (cfErr instanceof Error ? cfErr.message : String(cfErr)) + ". Contact support@piclo.art if the problem persists.");
        }

        const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(data.svg)}`;
        setFinalSvgUrl(dataUri);
        setPreviewThumbnailUrl(uriToProcess);
        setPreviewFullUrl(uriToProcess);
      } else {
        throw new Error(data.error || 'Failed to generate SVG. Contact support@piclo.art if the problem persists.');
      }

      setIsDirty(false);
      setIsSaveSuccess(false);
    } catch (err) {
      console.error('API Error:', err);
      setErrorMsg('Failed to process image. Please try again or contact support@piclo.art if the problem persists.');
      setModalState('ready');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async (override = false) => {
    if (!tempPrefix || !selectedPack || !puzzleName) return;

    setIsSaving(true);
    setErrorMsg(null);

    try {
      const response = await fetch(`${apiUrl}/api/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tempPrefix,
          packId: selectedPack,
          puzzleName: puzzleName.trim(),
          override
        }),
      });

      const data = await response.json();

      if (data.collision) {
        setCollisionName(data.existingName);
        setShowCollisionWarning(true);
        return;
      }

      if (data.success) {
        // Success! Show a checkmark in the button for 10 seconds
        setShowCollisionWarning(false);
        setIsSaveSuccess(true);
        setTimeout(() => {
          setIsSaveSuccess(false);
        }, 10000);
      } else {
        setIsSaveSuccess(false);
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Save Error:', err);
      setErrorMsg('Failed to save puzzle to pack.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setModalState(isAuthed ? 'ready' : 'locked');
    setIsDirty(false);
  };

  const handleRestoreDefaults = () => {
    setMaxColors(8);
    setMinDustArea(40);
    setRdpEpsilon(1.0);
    setCornerAngle(1.8);
    setMaxError(4.0);
    setAutoCrop(true);
    setIsDirty(true);
  };

  const effectiveTitle = hasPicloPlay ? 'Create a Custom Puzzle' : title;

  return (
    <>
      <BaseModal
        visible={visible}
        onClose={onClose}
        header={(
          <>
            {onBack ? (
              <View style={styles.headerRow}>
                <TouchableOpacity onPress={onBack} style={styles.backButton}>
                  <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
                </TouchableOpacity>
                <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>{effectiveTitle}</Text>
                <View style={{ width: 44 }} />
              </View>
            ) : (
              <Text style={modalStyles.headerText}>{effectiveTitle}</Text>
            )}
            {modalState !== 'ready' && (
              <Text style={styles.subtitleText}>Turn your own images into Piclo puzzles!</Text>
            )}
          </>
        )}
        footer={(
          <ModalStickyFooter style={modalStyles.buttonContainer}>
            {modalState === 'locked' && (
              <HoverButton
                style={modalStyles.primaryButton}
                hoverStyle={modalStyles.primaryButtonHover}
                textStyle={modalStyles.primaryButtonText}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    if ((window as any).Paddle?.Checkout) {
                      (window as any).Paddle.Checkout.open({ items: [{ priceId: 'pri_01ks79bqva52p3cbwd6m18ajn0', quantity: 1 }] });
                    }
                  } else {
                    setModalState('ready');
                    onSimulatePurchase?.();
                  }
                }}
              >
                $10 Unlock Piclo Play
              </HoverButton>
            )}


            {modalState === 'ready' && (
              <HoverButton
                style={modalStyles.primaryButton}
                hoverStyle={modalStyles.primaryButtonHover}
                textStyle={modalStyles.primaryButtonText}
                onPress={handleUpload}
              >
                Upload Image
              </HoverButton>
            )}


          </ModalStickyFooter>
        )}
      >

        {modalState === 'locked' && (
          <View style={styles.section}>
            {!hasPicloPlay ? (
              <>
                <View style={styles.imageContainer}>
                  {/* Placeholder for Dog image */}
                  <Image style={[styles.pipelineGraphic, isMobile && { height: 175 }]} source={require('../../../assets/images/pipeline-graphic-1.jpg')} resizeMode="contain" />
                </View>

                <View style={styles.valuePropsContainer}>
                  <Text style={styles.valuePropRow}>
                    <Text style={styles.valuePropText}> &mdash; Generate unlimited custom puzzles</Text>
                  </Text>
                  <Text style={styles.valuePropRow}>
                    <Text style={styles.valuePropText}> &mdash; Share puzzles with friends &amp; family</Text>
                  </Text>
                  <Text style={styles.valuePropRow}>
                    <Text style={styles.valuePropText}> &mdash; Unlock all past &amp; future puzzle packs</Text>
                  </Text>
                </View>
              </>
            ) : (
              <>
                <Text style={[modalStyles.headerText, { marginBottom: 8 }]}>Piclo Play Required</Text>
                <Text style={[styles.bodyText, { marginBottom: 20 }]}>
                  You need to unlock Piclo Play by clicking the link in your email to create and share your own custom puzzles.
                </Text>
              </>
            )}
          </View>
        )}

        {modalState === 'ready' && (
          <View style={styles.section}>
            {errorMsg && (
              <Text style={styles.errorText}>{errorMsg}</Text>
            )}

            {showCollisionWarning && collisionName && (
              <CollisionWarning
                name={collisionName}
                onConfirm={() => handleSave(true)}
                onCancel={() => setShowCollisionWarning(false)}
              />
            )}



            {(previewLayers.length > 0 || previewFullUrl || previewThumbnailUrl || selectedImageUri) && (
              <View style={[
                styles.previewContainer,
                isZoomed && styles.previewContainerZoomed,
                isZoomed && { aspectRatio: previewDimensions.width / previewDimensions.height }
              ]}>
                <TouchableOpacity
                  style={styles.zoomButton}
                  onPress={() => setIsZoomed(!isZoomed)}
                >
                  <FontAwesome6
                    name={isZoomed ? "magnifying-glass-minus" : "magnifying-glass-plus"}
                    size={18}
                    color={colors.text}
                  />
                </TouchableOpacity>
                {previewLayers.length > 0 ? (
                  <View
                    pointerEvents="none"
                    style={[styles.previewImage, isZoomed && styles.previewImageZoomed]}
                  >
                    <SvgCanvas
                      layers={previewLayers}
                      capturedLayerIds={[]}
                      activeLayerId={null}
                      onLayerPress={() => { }}
                      isTargetVisible={true}
                      width={previewDimensions.width}
                      height={previewDimensions.height}
                      preserveAspectRatio="xMidYMid meet"
                    />
                  </View>
                ) : (
                  <Image
                    source={{ uri: (previewFullUrl || previewThumbnailUrl || selectedImageUri) as string }}
                    style={[styles.previewImage, isZoomed && styles.previewImageZoomed]}
                    resizeMode="contain"
                  />
                )}
              </View>
            )}



            {!selectedImageUri && (
              <View style={[styles.imageContainer, isMobile && { marginBottom: 4 }]}>
                <Text style={styles.subtitleText}>Upload an image to get started</Text>
              </View>
            )}



            {modalState === 'ready' && !selectedImageUri && (
              <TouchableOpacity style={styles.imageContainer} onPress={handleUpload}>
                {/* Placeholder for Dog image */}

                <Image style={[styles.pipelineGraphic, isMobile && { height: 150 }]} source={require('../../../assets/images/pipeline-graphic-1.jpg')} resizeMode="contain" />

                <View style={styles.valuePropsContainer}>
                  <Text style={styles.valuePropRow}>
                    <Text style={styles.valuePropText}> &mdash; Generate unlimited custom puzzles</Text>
                  </Text>
                  <Text style={styles.valuePropRow}>
                    <Text style={styles.valuePropText}> &mdash; Share puzzles with friends &amp; family</Text>
                  </Text>
                  <Text style={styles.valuePropRow}>
                    <Text style={styles.valuePropText}> &mdash; All past &amp; future puzzle packs unlocked</Text>
                  </Text>
                </View>
              </TouchableOpacity>
            )}


            {selectedImageUri && (
              <View style={styles.controlsSection}>
                {/* URL Bar / Loading State moved under preview */}
                <View style={styles.sectionCenter}>
                  <View style={styles.urlWrapper}>
                    {isProcessing ? (
                      <View style={[styles.urlField, { justifyContent: 'center', gap: 10, borderStyle: 'dashed' }]}>
                        <ActivityIndicator size="small" color={colors.primary} />
                        <Text style={styles.loadingText}>Processing Image...</Text>
                      </View>
                    ) : (previewFullUrl || previewThumbnailUrl) && customUrl && !isDirty ? (
                      <>
                        <Text style={[styles.accordionTitle, { alignSelf: 'flex-start', marginBottom: 8 }]}>Share Link</Text>
                        {showCopyTooltip && (
                          <View style={styles.copyTooltipContainer} pointerEvents="none">
                            <View style={styles.copyTooltipBox}>
                              <Text style={styles.copyTooltipText}>Copied to clipboard</Text>
                            </View>
                            <View style={styles.copyTooltipArrow} />
                          </View>
                        )}
                        <Pressable style={styles.urlField} onPress={handleCopy}>
                          <Text style={styles.urlText}><FontAwesome6 name="circle-check" size={16} color="#157d3b" solid /> {customUrl}</Text>
                          <FontAwesome6 name="copy" size={16} color={colors.textMuted} />
                        </Pressable>
                      </>
                    ) : null}
                  </View>
                  {/* {(previewLayers.length > 0 || previewFullUrl || previewThumbnailUrl || selectedImageUri) && (
                    <View style={{ width: '100%', alignItems: 'left', marginBottom: 12 }}>
                      <Text style={{
                        ...typography.body,
                        fontSize: 16,
                        color: colors.textMuted,
                        fontWeight: 'normal',
                      }}>
                        Maximum 5 puzzles live at a time ({5 - customPuzzleCount} remaining)
                      </Text>
                    </View>
                  )} */}
                </View>

                <Pressable
                  style={styles.accordionHeader}
                  onPress={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
                >
                  <FontAwesome6
                    name={isAdvancedExpanded ? "minus" : "plus"}
                    size={14}
                    color={colors.textMuted}
                  />
                  <Text style={styles.accordionTitle}>Advanced Settings</Text>
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      setIsInfoModalVisible(true);
                    }}
                    style={{ padding: 4 }}
                  >
                  </Pressable>
                </Pressable>
                {isAdvancedExpanded && (
                  <View style={styles.slidersList}>
                    {/* <View style={styles.selectorContainer}>
                    <Text style={styles.selectorLabel}>Destination Pack</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        style={styles.webSelect}
                        value={selectedPack}
                        onChange={(e) => setSelectedPack(e.target.value)}
                      >
                        {packs.map((pack) => (
                          <option key={pack.id} value={pack.id}>
                            {pack.title}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <View style={styles.nativeSelectorPlaceholder}>
                        <Text style={styles.bodyText}>
                          {packs.find(p => p.id === selectedPack)?.title || 'Select a pack'}
                        </Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.selectorContainer}>
                    <Text style={styles.selectorLabel}>Puzzle Name</Text>
                    <TextInput
                      style={styles.textInput}
                      value={puzzleName}
                      onChangeText={(val) => { setPuzzleName(val); setIsSaveSuccess(false); }}
                      placeholder="e.g., Morning Coffee"
                      placeholderTextColor={colors.textMuted}
                    />
                  </View> */}

                    {/* <Pressable 
                    style={styles.toggleRow}
                    onPress={() => { setAutoCrop(!autoCrop); setIsDirty(true); setIsSaveSuccess(false); }}
                  >
                    <FontAwesome6 
                      name={autoCrop ? "square-check" : "square"} 
                      size={18} 
                      color={autoCrop ? colors.primary : colors.textMuted} 
                      solid={autoCrop}
                    />
                    <Text style={styles.toggleLabel}>Crop Image</Text>
                  </Pressable> */}
                    {/* <ParameterSlider
                    label="Max Colors"
                    value={maxColors}
                    min={2} max={16} step={1}
                    onValueChange={(v) => { setMaxColors(v); setIsDirty(true); setIsSaveSuccess(false); }}
                  /> */}
                    <Pressable style={styles.link} onPress={(e) => {
                      e.stopPropagation();
                      setIsInfoModalVisible(true);
                    }}>
                      <Text style={styles.linkText}>What are these settings?</Text>
                    </Pressable>
                    <ParameterSlider
                      label="Min Dust Area"
                      value={minDustArea}
                      min={0} max={200} step={5}
                      onValueChange={(v) => { setMinDustArea(v); setIsDirty(true); setIsSaveSuccess(false); }}
                    />
                    <ParameterSlider
                      label="RDP Epsilon"
                      value={rdpEpsilon}
                      min={0.1} max={5.0} step={0.1}
                      onValueChange={(v) => { setRdpEpsilon(v); setIsDirty(true); setIsSaveSuccess(false); }}
                    />
                    <ParameterSlider
                      label="Corner Angle (Rads)"
                      value={cornerAngle}
                      min={0.5} max={3.14} step={0.1}
                      onValueChange={(v) => { setCornerAngle(v); setIsDirty(true); setIsSaveSuccess(false); }}
                    />
                    <ParameterSlider
                      label="Max Error"
                      value={maxError}
                      min={0.5} max={20.0} step={0.5}
                      onValueChange={(v) => { setMaxError(v); setIsDirty(true); setIsSaveSuccess(false); }}
                    />
                    <Pressable style={styles.link} onPress={handleRestoreDefaults}>
                      <Text style={styles.linkText}>Restore Defaults</Text>
                    </Pressable>

                    {(!previewThumbnailUrl || isDirty) && (
                      <View style={{ marginTop: 12 }}>
                        <HoverButton
                          style={modalStyles.secondaryButton}
                          textStyle={modalStyles.secondaryButtonText}
                          onPress={() => handleProcess()}
                          disabled={isProcessing || isSaving || puzzleName.trim() === ''}
                        >
                          {isProcessing ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                              <ActivityIndicator size="small" color="#fff" />
                              <Text style={modalStyles.secondaryButtonText}>Processing...</Text>
                            </View>
                          ) : (
                            "Re-generate Puzzle"
                          )}
                        </HoverButton>
                      </View>
                    )}

                    {previewThumbnailUrl && !isDirty && (
                      <View style={{ marginTop: 12 }}>
                        <HoverButton
                          style={modalStyles.secondaryButton}
                          textStyle={modalStyles.secondaryButtonText}
                          onPress={() => {
                            if (finalSvgUrl && customPuzzleId) {
                              onPlayPuzzle?.(finalSvgUrl, true, customPuzzleId);
                            }
                          }}
                          disabled={isLoading}
                        >
                          {isLoading ? "Loading..." : "Play Puzzle"}
                        </HoverButton>
                      </View>
                    )}

                    {/* {previewThumbnailUrl && !isDirty && (
                    <View style={{ marginTop: 12 }}>
                      <HoverButton
                        style={[modalStyles.secondaryButton]}
                        textStyle={modalStyles.secondaryButtonText}
                        onPress={() => !isSaveSuccess && handleSave()}
                        disabled={isSaving || isProcessing}
                      >
                        {isSaving ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                            <ActivityIndicator size="small" />
                            <Text style={modalStyles.secondaryButtonText}>Saving...</Text>
                          </View>
                        ) : isSaveSuccess ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                            <FontAwesome6 name="check" size={16} />
                            <Text style={modalStyles.secondaryButtonText}>Saved to Pack</Text>
                          </View>
                        ) : (
                          "Save to Pack"
                        )}
                      </HoverButton>
                    </View>
                  )} */}
                  </View>
                )}
              </View>
            )}
          </View>
        )}
      </BaseModal>

      <BaseModal
        visible={showOverwriteWarning}
        onClose={() => setShowOverwriteWarning(false)}
        isFullScreenOnMobile={false}
        header={<Text style={modalStyles.headerText}>Storage Limit Reached</Text>}
        footer={(
          <ModalStickyFooter>
            <View style={{ gap: 12, width: '100%' }}>
              <HoverButton
                style={modalStyles.primaryButton}
                hoverStyle={modalStyles.primaryButtonHover}
                textStyle={modalStyles.primaryButtonText}
                onPress={resumeUpload}
              >
                Replace Oldest Puzzle
              </HoverButton>
              <HoverButton
                style={modalStyles.clearButton}
                textStyle={modalStyles.clearButtonText}
                onPress={() => setShowOverwriteWarning(false)}
              >
                Cancel
              </HoverButton>
            </View>
          </ModalStickyFooter>
        )}
      >
        <View style={{ padding: 16 }}>
          <Text style={{
            ...typography.body,
            color: colors.textMuted,
            textAlign: 'center',
            lineHeight: 24,
            fontSize: 16
          }}>
            You have reached the maximum of 5 custom puzzles live at a time. Creating a new one will remove your oldest custom puzzle, and any links you shared for it will stop working.
          </Text>
        </View>
      </BaseModal>

      <AdvancedSettingsInfoModal
        visible={isInfoModalVisible}
        onClose={() => setIsInfoModalVisible(false)}
        onBack={() => setIsInfoModalVisible(false)}
      />
    </>
  );
}

